import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import PaperDraggable from "../../../../../components/PaperDraggable";
import { formatarInputValorMonetário, formatarMonetárioParaNúmero, formatarValorMonetario } from "../../../../../helpers/masks";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SelectTitulosReceber from "../../../../../components/SelectsSimpleApiQuery/SelectTitulosReceber";
import { IDataListaTitulosReceber } from "../../../../../types/api/financeiro/pesquisar-titulos-receber";
import { Moment } from "moment";
import BasicDatePicker from "../../../../../components/BasicDatePicker";

export default function DialogAdicionarCupom({
    open,
    handleClose
}: DialogIncluirLancamentoProps) {

    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [valor, setValor] = useState("")
    const [titulos, setTitulos] = useState<IDataListaTitulosReceber[] | null>(null)
    const [dataValidade, setDataValidade] = useState<Moment | null>(null);

    //Querys e mutations
    const { mutate: criarCupom, isLoading } = useMutation({
        mutationKey: 'solicitar-criacao-cupom',
        mutationFn: (valor: TAdicionarCupomParams) => axiosClient.post("/financeiro/cupons/cadastrar-cupom", { ...valor }),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao cadastrar cupom!', { variant: 'error' });
        },
        onSuccess: () => {
            enqueueSnackbar(`Cupom criado com sucesso!`, { variant: 'success' });
            queryClient.invalidateQueries("pesquisar-cupons");
            navigate("/financeiro/cupons/listar-todos");
            handleClose();
        }
    })

    // Funções manipuladoras
    const onSubmit = () => {
        criarCupom({
            valor: formatarMonetárioParaNúmero(valor),
            titulos: titulos ? titulos.map(item => item.id) : [],
            data_validade: dataValidade ? dataValidade.format("YYYY-MM-DD") : ""
        });
    }

    // Valores importantes
    const btnDisabled = useMemo(() => {
        return isLoading || !valor || !titulos || titulos.length === 0 || !dataValidade;
    }, [isLoading, valor, titulos, dataValidade])

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={'xs'}
            fullWidth
            PaperComponent={PaperDraggable}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Adicionar cupom</DialogTitle>

            <DialogContent>
                <form onSubmit={onSubmit}>
                    <Stack spacing={2} mt={2}>
                        {
                            titulos &&
                            <>
                                {titulos.map(item => (
                                    <Typography key={item.id}>{item.id} - Valor: {formatarValorMonetario(item.saldo_principal)}</Typography>
                                ))}
                                <Typography>Total: {formatarValorMonetario(titulos.reduce((acc, item) => acc + item.saldo_principal, 0))}</Typography>
                                {valor && (
                                    <Typography color="primary" fontWeight="bold">
                                        Desconto: {((formatarMonetárioParaNúmero(valor) / titulos.reduce((acc, item) => acc + item.saldo_principal, 0)) * 100).toFixed(2)}%
                                    </Typography>
                                )}
                            </>
                        }
                        <TextField
                            label="Valor"
                            value={valor}
                            onChange={(e) => setValor(formatarInputValorMonetário(e.target.value))}
                            fullWidth
                        />
                        <SelectTitulosReceber
                            value={titulos}
                            onChange={(data) => setTitulos(data as IDataListaTitulosReceber[] | null)}
                            extraParams={{ data_recebimento_is_null: true }}
                            multiple
                        />
                        <BasicDatePicker
                            blockPastDates
                            label="Data de Validade"
                            value={dataValidade}
                            onChange={setDataValidade}
                        />

                    </Stack>
                </form>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="text"
                    onClick={handleClose}
                >
                    Fechar
                </Button>
                <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={btnDisabled}
                >
                    Adicionar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export interface DialogIncluirLancamentoProps {
    open: boolean;
    handleClose: () => void
}

type TAdicionarCupomParams = {
    valor: number;
    titulos: string[];
    data_validade: string;
}
